import { Box } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import * as FSQ from "src/generated/hooks/form-service/queries";
import {
  createOfferingCategories,
  deleteOfferingCategory,
  updateOfferingCategory,
  type OfferingCategory,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useDeleteConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { OfferingCategoryEditForm } from "./OfferingCategoryEditForm";
import {
  emptyCategory,
  toCreatePayload,
  toFormValues,
  toUpdatePayload,
  type OfferingCategoryFormValues,
} from "./offeringCategoryForm";
import { OfferingCategoryView } from "./OfferingCategoryView";

type SavedCardProps = {
  enrollmentPeriodId: string;
  category: OfferingCategory;
  siblingKeys: readonly string[];
};

type DraftCardProps = {
  enrollmentPeriodId: string;
  siblingKeys: readonly string[];
  onDiscard: () => void;
};

export const OfferingCategoryCard: React.FC<SavedCardProps> = ({
  enrollmentPeriodId,
  category,
  siblingKeys,
}) => {
  const toast = useAvelaToast();
  const invalidateCategories = useInvalidateOfferingCategories();
  const [isEditing, setIsEditing] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const { confirm, confirmationDialog } = useDeleteConfirmationDialog({
    header: "Delete offering category?",
    body: "This will permanently delete this offering category and all of its options. You can't undo this action afterwards.",
  });

  const handleSubmit = async (values: OfferingCategoryFormValues) => {
    try {
      await updateOfferingCategory({
        path: {
          enrollmentPeriodId,
          offeringCategoryId: category.id,
        },
        body: toUpdatePayload(values),
      });
    } catch (err) {
      console.error(err);
      toast.error({
        id: "update-offering-category",
        title: "Failed to update offering category",
      });
      return;
    }

    toast({
      id: "update-offering-category",
      title: "Offering category updated",
    });
    setIsEditing(false);
    await invalidateCategories();
  };

  const handleDelete = async () => {
    if (!(await confirm())) return;

    setIsDeleting(true);
    try {
      await deleteOfferingCategory({
        path: {
          enrollmentPeriodId,
          offeringCategoryId: category.id,
        },
      });
      toast({
        id: "delete-offering-category",
        title: "Offering category deleted",
      });
      await invalidateCategories();
    } catch (err) {
      console.error(err);
      toast.error({
        id: "delete-offering-category",
        title: "Failed to delete offering category",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      {confirmationDialog}
      <CategoryCardShell>
        {isEditing ? (
          <OfferingCategoryEditForm
            initialValues={toFormValues(category)}
            siblingKeys={siblingKeys}
            submitLabel="Update offering category"
            isDeleting={isDeleting}
            onSubmit={handleSubmit}
            onCancel={() => setIsEditing(false)}
            onDelete={() => {
              void handleDelete();
            }}
          />
        ) : (
          <OfferingCategoryView
            category={category}
            onEdit={() => setIsEditing(true)}
          />
        )}
      </CategoryCardShell>
    </>
  );
};

export const OfferingCategoryDraftCard: React.FC<DraftCardProps> = ({
  enrollmentPeriodId,
  siblingKeys,
  onDiscard,
}) => {
  const toast = useAvelaToast();
  const invalidateCategories = useInvalidateOfferingCategories();

  const handleSubmit = async (values: OfferingCategoryFormValues) => {
    try {
      await createOfferingCategories({
        path: { enrollmentPeriodId },
        body: [toCreatePayload(values)],
      });
    } catch (err) {
      console.error(err);
      toast.error({
        id: "create-offering-category",
        title: "Failed to create offering category",
      });
      return;
    }

    toast({
      id: "create-offering-category",
      title: "Offering category created",
    });
    onDiscard();
    await invalidateCategories();
  };

  return (
    <CategoryCardShell>
      <OfferingCategoryEditForm
        initialValues={emptyCategory()}
        siblingKeys={siblingKeys}
        submitLabel="Create offering category"
        onSubmit={handleSubmit}
        onCancel={onDiscard}
        onDelete={onDiscard}
      />
    </CategoryCardShell>
  );
};

function CategoryCardShell({ children }: { children: React.ReactNode }) {
  return (
    <Box
      width="100%"
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="xl"
    >
      {children}
    </Box>
  );
}

function useInvalidateOfferingCategories() {
  const queryClient = useQueryClient();
  return React.useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: [FSQ.useGetOfferingCategoriesKey],
      }),
    [queryClient]
  );
}
